import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { X, Loader2, Play, Send, CheckCircle2, XCircle, Clock } from "lucide-react";

const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

export const RECOVERY_STATUS = {
  healthy: { label: "Sending OK", tone: "ok" },
  never_run: { label: "Waiting for first run", tone: "warn" },
  stale: { label: "Job not running", tone: "err" },
  failing: { label: "Sends failing", tone: "err" },
  gmail_down: { label: "Gmail disconnected", tone: "err" },
  error: { label: "Check failed", tone: "err" },
};

// "Is the 24h come-back-to-your-quote email really going out?" — last runs, every send, what's due.
export function RecoveryEmailsModal({ open, onClose, isAdmin }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/admin/recovery-emails").then(r => setData(r.data)).catch(() => toast.error("Could not load recovery-email status")), []);
  useEffect(() => { if (open) { setData(null); load(); } }, [open, load]);
  if (!open) return null;
  const h = data?.health || {};
  const st = RECOVERY_STATUS[h.status] || RECOVERY_STATUS.error;
  const last = h.last || {};

  const runNow = async () => {
    setBusy("run");
    try {
      const r = await api.post("/admin/recovery-emails/run");
      toast.success(`Job ran — ${r.data.sent} sent · ${r.data.failed} failed · ${r.data.due} were due${r.data.gmail_connected ? "" : " (Gmail not connected — nothing attempted)"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not run the job"); } finally { setBusy(""); }
  };
  const testSend = async () => {
    setBusy("test");
    try {
      const r = await api.post("/admin/recovery-emails/test", {});
      if (r.data.sent) toast.success(`Test recovery email sent to ${r.data.to} — check your inbox`);
      else toast.error(`Test send FAILED: ${r.data.error || "unknown error"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the test"); } finally { setBusy(""); }
  };

  return (
    <div data-testid="recovery-emails-modal" className="fixed inset-0 z-50 bg-[#12263A]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E7EB]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold">System check</div>
            <div className="text-base font-black text-[#12263A]">24-hour quote recovery emails</div>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="recovery-status-pill" className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 ${st.tone === "ok" ? "bg-[#D8EED8] text-[#1F6B3B]" : st.tone === "warn" ? "bg-[#FFF4DE] text-[#A87E28]" : "bg-[#FDE2E1] text-[#C0392B]"}`}>{st.label}</span>
            <button data-testid="recovery-modal-close" onClick={onClose} className="p-1 text-[#6B7280] hover:text-[#12263A]" aria-label="Close"><X size={18} /></button>
          </div>
        </div>
        {!data ? <div className="p-10 text-center text-[#6B7280]"><Loader2 className="animate-spin inline" /> Loading…</div> : (
          <div className="overflow-y-auto p-5 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Stat label="Last run" value={fmt(h.last_run_at)} sub={last.ran_at ? `${last.due ?? 0} due · ${last.sent ?? 0} sent · ${last.failed ?? 0} failed` : "hourly, first pass 2 min after boot"} testid="recovery-last-run" />
              <Stat label="Sent (7 days)" value={h.sent_7d ?? 0} tone="ok" testid="recovery-sent-7d" />
              <Stat label="Failed (7 days)" value={h.failed_7d ?? 0} tone={h.failed_7d ? "err" : ""} testid="recovery-failed-7d" />
              <Stat label="Due now" value={h.due ?? 0} sub="leads 24h–7d old, not yet emailed" testid="recovery-due" />
            </div>
            {isAdmin && (
              <div className="flex flex-wrap gap-2">
                <button data-testid="recovery-run-now" disabled={!!busy} onClick={runNow} className="de-btn-secondary text-xs"><Play size={14} /> {busy === "run" ? "Running…" : "Run the job now"}</button>
                <button data-testid="recovery-test-send" disabled={!!busy} onClick={testSend} className="de-btn-primary text-xs"><Send size={14} /> {busy === "test" ? "Sending…" : "Send me a test recovery email"}</button>
                <div className="text-[11px] text-[#6B7280] self-center">The test goes to your admin address via Gmail — if it lands, real ones do too.</div>
              </div>
            )}
            <SendLog rows={data.log || []} />
            <Pending rows={data.pending || []} />
            <Runs rows={data.runs || []} />
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub, tone, testid }) {
  return (
    <div className="border border-[#E5E7EB] p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold">{label}</div>
      <div className={`text-xl font-black ${tone === "err" ? "text-[#C0392B]" : tone === "ok" ? "text-[#1F6B3B]" : "text-[#12263A]"}`}>{value}</div>
      {sub && <div className="text-[11px] text-[#6B7280]">{sub}</div>}
    </div>
  );
}

function SendLog({ rows }) {
  return (
    <section data-testid="recovery-send-log">
      <div className="label-eyebrow mb-2">Every recovery email (latest 25)</div>
      {rows.length === 0 ? <div className="text-xs text-[#9CA3AF] border border-dashed border-[#E5E7EB] p-4">No recovery emails have been attempted yet.</div> : (
        <div className="border border-[#E5E7EB] divide-y divide-[#F0EAE0] text-xs">
          {rows.map((r, i) => (
            <div key={`${r.created_at}-${i}`} className="flex items-center gap-3 px-3 py-2 flex-wrap" data-testid={`recovery-log-${i}`}>
              {r.status === "sent" ? <CheckCircle2 size={14} className="text-[#1F6B3B] shrink-0" /> : <XCircle size={14} className="text-[#C0392B] shrink-0" />}
              <span className="w-28 shrink-0 text-[#6B7280]">{fmt(r.created_at)}</span>
              <span className="font-semibold text-[#12263A] truncate">{r.to}</span>
              {r.kind === "abandoned_resume_test" && <span className="text-[9px] font-black uppercase tracking-widest border border-[#E5E7EB] px-1">test</span>}
              <span className={`basis-full sm:basis-auto sm:ml-auto shrink-0 font-bold ${r.status === "sent" ? "text-[#1F6B3B]" : "text-[#C0392B]"}`}>{r.status === "sent" ? "Sent" : `Failed${r.error ? ` — ${r.error}` : ""}`}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Pending({ rows }) {
  return (
    <section data-testid="recovery-pending">
      <div className="label-eyebrow mb-2">Due for a recovery email now ({rows.length})</div>
      {rows.length === 0 ? <div className="text-xs text-[#9CA3AF] border border-dashed border-[#E5E7EB] p-4">Nobody is waiting — every eligible visitor has been emailed.</div> : (
        <div className="border border-[#E5E7EB] divide-y divide-[#F0EAE0] text-xs">
          {rows.map(p => (
            <div key={p.email} className="flex items-center gap-3 px-3 py-2 flex-wrap">
              <Clock size={13} className="text-[#C79A3B] shrink-0" />
              <span className="font-semibold text-[#12263A]">{p.name || "—"}</span>
              <span className="text-[#6B7280]">{p.email}</span>
              <span className="text-[#9CA3AF]">left at {p.step || "postcode"} · {fmt(p.last_activity)}</span>
              {p.attempts > 0 && <span className="ml-auto text-[#C0392B] font-bold">{p.attempts} failed attempt{p.attempts > 1 ? "s" : ""}{p.last_error ? ` — ${p.last_error}` : ""}</span>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Runs({ rows }) {
  if (!rows.length) return null;
  return (
    <section data-testid="recovery-runs">
      <div className="label-eyebrow mb-2">Recent job runs</div>
      <div className="border border-[#E5E7EB] divide-y divide-[#F0EAE0] text-xs">
        {rows.map(r => (
          <div key={r.ran_at} className="flex items-center gap-3 px-3 py-1.5">
            <span className="w-28 shrink-0 text-[#6B7280]">{fmt(r.ran_at)}</span>
            <span>{r.due ?? 0} due · <b className="text-[#1F6B3B]">{r.sent ?? 0} sent</b> · <b className={r.failed ? "text-[#C0392B]" : ""}>{r.failed ?? 0} failed</b> · {r.enquiries_created ?? 0} enquiries</span>
            <span className={`ml-auto text-[10px] font-black uppercase tracking-widest ${r.gmail_connected ? "text-[#1F6B3B]" : "text-[#C0392B]"}`}>{r.gmail_connected ? "Gmail ok" : "Gmail off"}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
